'use client';

/**
 * 时间窗口列表 Hook
 * 首次以初始 slug 为中心生成窗口列表，之后切换只更新选中项，不重建列表。
 * 当选中项超出列表范围时动态扩展。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from '@/lib/i18n';
import { formatDateTime } from '@/lib/utils/date';
import customFetch from '@/lib/utils/fetch';

// ==================== 类型定义 ====================

export type UpdownChartInterval = '5m' | '15m' | '1h' | '4h' | '1d';

export interface TimeWindowItem {
  slug: string;
  startTs: number;
  endTs?: number;
  label: string;
  token: string;
  active: boolean;
}

interface ParsedUpdownSlug {
  token: string;
  type: string;
  interval: UpdownChartInterval;
  startTs: number;
}

interface GammaEventTagLike {
  forceShow?: boolean;
  id?: number | string;
  slug?: string;
}

interface GammaEventSeriesLike {
  recurrence?: string;
  slug?: string;
}

interface GammaEventLike {
  createdAt?: string;
  endDate?: string;
  endDateIso?: string;
  series?: GammaEventSeriesLike[];
  seriesSlug?: string;
  slug: string;
  startDate?: string;
  startDateIso?: string;
  tags?: GammaEventTagLike[];
}

interface GammaEventWindow {
  slug: string;
  startTs: number;
  endTs: number;
}

interface GammaEventWindowGroup {
  relatedWindows: GammaEventWindow[];
}

interface FetchGammaEventSlugGroupOptions {
  includeCurrent?: boolean;
  limit?: number;
  radius?: number;
  tagSlug?: string;
}

// ==================== 常量 ====================

/** 每次向前/向后扩展的窗口数量 */
const EXTEND_COUNT = 20;
/** 初始向前生成的窗口数量 */
const INITIAL_PAST_COUNT = 20;
/** 初始向后生成的窗口数量 */
const INITIAL_FUTURE_COUNT = 10;
const DEFAULT_INTERVAL: UpdownChartInterval = '15m';

const PAST_RESULTS_API = '/api/polymarket/past-results';
const POLYMARKET_GAMMA_EVENTS_API = 'https://gamma-api.polymarket.com/events';

/** Map i18next language key → BCP 47 locale tag */
const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  zh: 'zh-CN',
  zh_CN: 'zh-CN',
  zh_TW: 'zh-TW',
  ja: 'ja-JP',
  ko: 'ko-KR',
};

const MONTH_LABELS: Record<string, string> = {
  january: 'Jan',
  february: 'Feb',
  march: 'Mar',
  april: 'Apr',
  may: 'May',
  june: 'Jun',
  july: 'Jul',
  august: 'Aug',
  september: 'Sep',
  october: 'Oct',
  november: 'Nov',
  december: 'Dec',
};

const MONTH_NAMES = Object.keys(MONTH_LABELS);

const TOKEN_MAP: Record<string, string> = {
  BITCOIN: 'BTC',
  ETHEREUM: 'ETH',
  SOLANA: 'SOL',
  XRP: 'XRP',
  DOGECOIN: 'DOGE',
  BNB: 'BNB',
  HYPERLIQUID: 'HYPE',
  CARDANO: 'ADA',
  POLKADOT: 'DOT',
};

const TOKEN_SLUG_MAP: Record<string, string> = {
  BTC: 'bitcoin',
  ETH: 'ethereum',
  SOL: 'solana',
  XRP: 'xrp',
  DOGE: 'dogecoin',
  BNB: 'bnb',
  HYPE: 'hyperliquid',
  ADA: 'cardano',
  DOT: 'polkadot',
};

export const supportCryptos = [
  'bitcoin',
  'ethereum',
  'solana',
  'xrp',
  'dogecoin',
  'bnb',
  'hyperliquid',
] as const;

const GENERIC_EVENT_TAG_SLUGS = new Set([
  'recurring',
  'hide-from-new',
  'crypto',
  'crypto-prices',
  'weekly',
  'daily',
  'monthly',
  'today',
]);

const ASSET_EVENT_TAG_SLUGS = new Set<string>(supportCryptos);

// ==================== Hook 主体 ====================

export function useTimeWindowList(selectedSlug: string) {
  const { currentLanguage } = useTranslation();
  const locale = getDateLocale(currentLanguage);

  // 记住初始 slug 作为列表中心，不随选中变化
  const initialSlugRef = useRef(selectedSlug);
  const initialSlugFormat = initialSlugRef.current.includes('-up-or-down-') ? 'long' : 'short';
  const initialParsed = useMemo(() => parseUpdownSlug(initialSlugRef.current), []);
  const usesSeriesSlugSource = !initialParsed;

  const [pastExtra, setPastExtra] = useState(0);
  const [futureExtra, setFutureExtra] = useState(0);
  const [seriesWindows, setSeriesWindows] = useState<TimeWindowItem[]>([]);
  const [outcomesBySlug, setOutcomesBySlug] = useState<Record<string, string>>({});

  const [nowWindowStartTs, setNowWindowStartTs] = useState<number>(() => {
    if (!initialParsed) return -1;
    const intervalSec = getIntervalMilliseconds(initialParsed.interval) / 1000;
    return Math.floor(Math.floor(Date.now() / 1000 / intervalSec) * intervalSec);
  });

  // 静态快照，用于 liveOffsetFromInitial 窗口偏移计算（避免窗口列表随 live 窗口移动而重建）
  const initialNowWindowStartTs = useRef(nowWindowStartTs).current;
  const lastFetchedNowRef = useRef(0);

  // series slug 来源：通过 Gamma API 获取同 series 的所有窗口
  useEffect(() => {
    if (!usesSeriesSlugSource || !initialSlugRef.current) return;

    const controller = new AbortController();
    fetchGammaEventSlugGroupBySlug(initialSlugRef.current, { signal: controller.signal })
      .then((group) => {
        if (controller.signal.aborted) return;
        const windows =
          group.relatedWindows.length > 0
            ? group.relatedWindows
            : [{ slug: initialSlugRef.current, startTs: 1, endTs: 0 }];
        setSeriesWindows(generateSeriesWindows(windows));
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.warn('[useTimeWindowList] fetch series slugs failed:', error);
        setSeriesWindows(generateSeriesWindows([{ slug: initialSlugRef.current }]));
      });

    return () => controller.abort();
  }, [usesSeriesSlugSource]);

  // series 数据加载后，根据当前时间定位 live 窗口
  useEffect(() => {
    if (!usesSeriesSlugSource || seriesWindows.length === 0) return;

    const nowSec = Math.floor(Date.now() / 1000);
    const liveWindow =
      seriesWindows.find((win) => {
        if (!win.endTs || win.endTs <= 0) return false;
        return win.startTs <= nowSec && nowSec <= win.endTs;
      }) ??
      seriesWindows.find((win) => win.endTs && win.endTs >= nowSec) ??
      seriesWindows[seriesWindows.length - 1];

    setNowWindowStartTs(liveWindow.startTs);
  }, [usesSeriesSlugSource, seriesWindows]);

  const liveOffsetFromInitial = useMemo(() => {
    if (!initialParsed || initialNowWindowStartTs < 0) return 0;

    const intervalSec = getIntervalMilliseconds(initialParsed.interval) / 1000;
    return Math.round((initialNowWindowStartTs - initialParsed.startTs) / intervalSec);
  }, [initialParsed, initialNowWindowStartTs]);

  const effectivePastCount = Math.max(
    INITIAL_PAST_COUNT + pastExtra,
    liveOffsetFromInitial < 0 ? Math.abs(liveOffsetFromInitial) : 0,
  );

  const effectiveFutureCount = Math.max(
    INITIAL_FUTURE_COUNT + futureExtra,
    liveOffsetFromInitial > 0 ? liveOffsetFromInitial : 0,
  );

  const interval = initialParsed?.interval ?? DEFAULT_INTERVAL;
  const intervalSec = useMemo(() => getIntervalMilliseconds(interval) / 1000, [interval]);

  const baseWindows = useMemo(() => {
    if (usesSeriesSlugSource) return seriesWindows;
    if (!initialParsed) return [];

    return generateWindows(
      initialParsed.token,
      initialParsed.type,
      initialParsed.interval,
      initialParsed.startTs,
      effectivePastCount,
      effectiveFutureCount,
      locale,
      initialSlugFormat,
    );
  }, [
    usesSeriesSlugSource,
    seriesWindows,
    initialParsed,
    effectivePastCount,
    effectiveFutureCount,
    locale,
    initialSlugFormat,
  ]);

  // 为列表中第一个未结束的窗口打上 active 标记，用于消费者定位 live 窗口
  const windows = useMemo(() => {
    const nowSec = Math.floor(Date.now() / 1000);
    let activeMarked = false;

    return baseWindows.map((win) => {
      const active = !activeMarked && getWindowEndTs(win, intervalSec) > nowSec;
      if (active) activeMarked = true;
      return { ...win, active };
    });
  }, [baseWindows, intervalSec, nowWindowStartTs]);

  // 解析选中 slug 的 startTs，用于匹配（兼容长格式和短格式 slug）
  const selectedStartTs = useMemo(() => {
    if (usesSeriesSlugSource) {
      return windows.find((win) => win.slug === selectedSlug)?.startTs ?? -1;
    }

    return parseUpdownSlug(selectedSlug)?.startTs ?? -1;
  }, [usesSeriesSlugSource, windows, selectedSlug]);

  // 选中项在列表中的 index（按 startTs 匹配，避免 slug 格式不一致的问题）
  const selectedIndex = useMemo(() => {
    if (selectedStartTs < 0) return -1;
    return usesSeriesSlugSource
      ? windows.findIndex((win) => win.slug === selectedSlug)
      : windows.findIndex((win) => win.startTs === selectedStartTs);
  }, [usesSeriesSlugSource, windows, selectedStartTs, selectedSlug]);

  // 如果选中项超出列表范围，自动扩展
  useEffect(() => {
    if (usesSeriesSlugSource) return;
    if (selectedIndex >= 0 || windows.length === 0) return;

    const parsed = parseUpdownSlug(selectedSlug);
    if (!parsed || !initialParsed) return;

    const intervalSecLocal = getIntervalMilliseconds(initialParsed.interval) / 1000;
    const diff = (parsed.startTs - initialParsed.startTs) / intervalSecLocal;

    if (diff < 0) {
      // 需要向过去扩展
      const needed = Math.abs(diff) - INITIAL_PAST_COUNT - pastExtra;
      if (needed > 0) {
        setPastExtra((prev) => prev + Math.ceil(needed / EXTEND_COUNT) * EXTEND_COUNT);
      }
      return;
    }

    // 需要向未来扩展
    const needed = diff - INITIAL_FUTURE_COUNT - futureExtra;
    if (needed > 0) {
      setFutureExtra((prev) => prev + Math.ceil(needed / EXTEND_COUNT) * EXTEND_COUNT);
    }
  }, [
    usesSeriesSlugSource,
    selectedSlug,
    selectedIndex,
    windows.length,
    initialParsed,
    pastExtra,
    futureExtra,
  ]);

  const loadMorePast = useCallback(() => {
    setPastExtra((prev) => prev + EXTEND_COUNT);
  }, []);

  const loadMoreFuture = useCallback(() => {
    setFutureExtra((prev) => prev + EXTEND_COUNT);
  }, []);

  // 历史窗口 outcome 获取
  useEffect(() => {
    if (nowWindowStartTs <= 0 || windows.length === 0) return;
    if (lastFetchedNowRef.current === nowWindowStartTs) return;
    lastFetchedNowRef.current = nowWindowStartTs;

    const pastSlugs = windows
      .filter((win) => win.startTs < nowWindowStartTs)
      .map((win) => win.slug);

    if (pastSlugs.length === 0) return;

    const controller = new AbortController();
    fetchPastOutcomes(pastSlugs, controller.signal).then((outcomes) => {
      if (controller.signal.aborted || Object.keys(outcomes).length === 0) return;
      setOutcomesBySlug((prev) => ({ ...prev, ...outcomes }));
    });

    return () => controller.abort();
  }, [nowWindowStartTs, windows]);

  return {
    windows,
    selectedIndex,
    selectedStartTs,
    nowWindowStartTs,
    hasLiveWindow: true,
    usesSeriesSlugSource,
    interval,
    intervalSec,
    outcomesBySlug,
    loadMorePast,
    loadMoreFuture,
  };
}

// ==================== Polymarket past-results API ====================

async function fetchPastOutcomes(
  pastEventSlugs: string[],
  signal?: AbortSignal,
): Promise<Record<string, string>> {
  if (pastEventSlugs.length === 0) return {};

  try {
    const response = await customFetch(PAST_RESULTS_API, {
      method: 'POST',
      body: JSON.stringify({ pastEventSlugs }),
      signal,
    });

    if (!response.ok) return {};

    const data = await response.json();
    return data?.data?.outcomesBySlug ?? {};
  } catch {
    return {};
  }
}

// ==================== Slug / 时间窗口工具 ====================

function buildSlug(
  token: string,
  type: string,
  interval: string,
  startTs: number,
  slugFormat: 'short' | 'long',
): string {
  if (slugFormat === 'long') {
    return formatLongSlug(token, startTs);
  }

  return `${token.toLowerCase()}-${type}-${interval}-${startTs}`;
}

function formatLongSlug(token: string, startTs: number): string {
  const tokenSlug = TOKEN_SLUG_MAP[token.toUpperCase()] ?? token.toLowerCase();
  const date = new Date(startTs * 1000);
  const utcMonth = date.getUTCMonth() + 1;
  const utcDay = date.getUTCDate();
  const etOffsetHours = isEasternDaylightTime(utcMonth, utcDay) ? -4 : -5;
  const etDate = new Date(date.getTime() + etOffsetHours * 60 * 60 * 1000);
  const month = MONTH_NAMES[etDate.getUTCMonth()];
  const day = etDate.getUTCDate();
  const year = etDate.getUTCFullYear();
  const hour = etDate.getUTCHours();
  const hour12 = hour % 12 || 12;
  const period = hour >= 12 ? 'pm' : 'am';

  return `${tokenSlug}-up-or-down-${month}-${day}-${year}-${hour12}${period}-et`;
}

function getDateLocale(lang: string): string {
  return LOCALE_MAP[lang] ?? LOCALE_MAP[lang.replace('-', '_')] ?? 'en-US';
}

function formatWindowLabel(
  startTs: number,
  interval: UpdownChartInterval,
  locale: string,
): string {
  const startDate = new Date(startTs);
  const endDate = new Date(startTs + getIntervalMilliseconds(interval));

  const formatTime = (date: Date) => {
    const hour = date.getHours();
    const minute = date.getMinutes().toString().padStart(2, '0');
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = (hour % 12 || 12).toString().padStart(2, '0');
    return `${hour12}:${minute} ${period}`;
  };

  const formatDay = (date: Date) =>
    date.toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
    });

  switch (interval) {
    case '5m':
    case '15m':
      return formatTime(endDate);
    case '1h':
      return formatTime(startDate);
    case '4h':
      return formatTime(endDate);
    case '1d':
      return formatDay(startDate);
    default:
      return formatTime(endDate);
  }
}

function generateWindows(
  token: string,
  type: string,
  interval: UpdownChartInterval,
  centerStartTs: number,
  pastCount: number,
  futureCount: number,
  locale: string,
  slugFormat: 'short' | 'long',
): TimeWindowItem[] {
  const intervalSec = getIntervalMilliseconds(interval) / 1000;
  const items: TimeWindowItem[] = [];

  for (let i = -pastCount; i <= futureCount; i += 1) {
    const ts = centerStartTs + i * intervalSec;
    if (ts <= 0) continue;

    items.push({
      slug: buildSlug(token, type, interval, ts, slugFormat),
      startTs: ts,
      endTs: ts + intervalSec,
      label: formatWindowLabel(ts * 1000, interval, locale),
      token,
      active: false,
    });
  }

  return items;
}

function formatSeriesSlugLabel(slug: string): string {
  const parts = slug.toLowerCase().split('-');
  const monthIndex = parts.findIndex((part) => MONTH_LABELS[part]);

  if (monthIndex >= 0) {
    const month = MONTH_LABELS[parts[monthIndex]];
    const next = parts[monthIndex + 1];
    const year = parts[monthIndex + 2];

    if (/^\d{1,2}$/.test(next ?? '')) return `${month} ${Number(next)}`;
    if (/^\d{4}$/.test(year ?? '')) return `${month} ${year}`;
    return month;
  }

  const beforeIndex = parts.indexOf('before');
  if (beforeIndex >= 0 && parts[beforeIndex + 1]) {
    return `Before ${parts[beforeIndex + 1]}`;
  }

  return slug.split('-').filter(Boolean).slice(-3).join(' ');
}

export function formatEndTime(endts: number): string {
  if (dayjs().isSame(endts, 'year')) {
    return formatDateTime(endts, 'MMM D');
  }
  return formatDateTime(endts, 'MMM D, YYYY');
}

function generateSeriesWindows(
  windows: { slug: string; startTs?: number; endTs?: number }[],
): TimeWindowItem[] {
  return windows.map((win, index) => ({
    slug: win.slug,
    startTs: win.startTs && win.startTs > 0 ? win.startTs : index + 1,
    endTs: win.endTs,
    label: formatSeriesSlugLabel(win.slug),
    token: '',
    active: false,
  }));
}

function parseUpdownSlug(slug: string): ParsedUpdownSlug | undefined {
  if (!slug || typeof slug !== 'string') return undefined;

  // 短格式：btc-updown-5m-1776738900
  if (slug.includes('-updown-')) {
    const parts = slug.split('-');
    if (parts.length < 4 || parts[1] !== 'updown') return undefined;

    const interval = parts[2] as UpdownChartInterval;
    const timestamp = Number.parseInt(parts[3], 10);
    if (!isUpdownChartInterval(interval) || !Number.isFinite(timestamp) || timestamp <= 0) {
      return undefined;
    }

    return {
      token: parts[0].toUpperCase(),
      type: 'updown',
      interval,
      startTs: timestamp,
    };
  }

  // 长格式：bitcoin-up-or-down-april-21-2026-12am-et
  if (!slug.includes('-up-or-down-')) return undefined;

  const parts = slug.split('-');
  let token = parts[0].toUpperCase();
  token = TOKEN_MAP[token] ?? token;

  const monthIndex = parts.findIndex((part) => MONTH_NAMES.includes(part.toLowerCase()));
  if (monthIndex === -1 || monthIndex + 2 >= parts.length) return undefined;

  const month = parts[monthIndex].toLowerCase();
  const day = parts[monthIndex + 1];
  const year = parts[monthIndex + 2];
  const timeStr = parts[monthIndex + 3];
  const monthNum = MONTH_NAMES.indexOf(month) + 1;
  const dayNum = Number.parseInt(day, 10);
  const yearNum = Number.parseInt(year, 10);
  if (monthNum <= 0 || !Number.isFinite(dayNum) || !Number.isFinite(yearNum)) return undefined;

  let hour = 0;
  const timeMatch = timeStr?.match(/^(\d+)(am|pm)$/i);
  if (timeMatch) {
    hour = Number.parseInt(timeMatch[1], 10);
    const period = timeMatch[2].toLowerCase();
    if (period === 'pm' && hour !== 12) hour += 12;
    if (period === 'am' && hour === 12) hour = 0;
  }

  const etOffset = isEasternDaylightTime(monthNum, dayNum) ? '-04:00' : '-05:00';
  const date = new Date(
    `${year}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(
      2,
      '0',
    )}T${String(hour).padStart(2, '0')}:00:00${etOffset}`,
  );

  if (Number.isNaN(date.getTime())) return undefined;

  return {
    token,
    type: 'updown',
    interval: '1h',
    startTs: Math.floor(date.getTime() / 1000),
  };
}

/**
 * 判断指定日期在美国东部时区是否处于夏令时
 * 美国夏令时：3月第二个周日 02:00 至 11月第一个周日 02:00
 * 简化处理：3月8号之后都算夏令时，11月7号之前都算夏令时
 */
function isEasternDaylightTime(month: number, day: number): boolean {
  if (month >= 4 && month <= 10) return true;
  if (month <= 2 || month === 12) return false;
  if (month === 3) return day >= 8;
  if (month === 11) return day < 7;
  return false;
}

function isUpdownChartInterval(interval: string): interval is UpdownChartInterval {
  return ['5m', '15m', '1h', '4h', '1d'].includes(interval);
}

function getIntervalMilliseconds(interval: UpdownChartInterval): number {
  const intervalMap: Record<UpdownChartInterval, number> = {
    '5m': 5 * 60 * 1000,
    '15m': 15 * 60 * 1000,
    '1h': 60 * 60 * 1000,
    '4h': 4 * 60 * 60 * 1000,
    '1d': 24 * 60 * 60 * 1000,
  };
  return intervalMap[interval];
}

function getWindowEndTs(win: TimeWindowItem, intervalSec: number): number {
  return win.endTs ?? win.startTs + intervalSec;
}

// ==================== Gamma API：同 series 事件查找 ====================

async function fetchGammaEventSlugGroupBySlug(
  slug: string,
  options: FetchGammaEventSlugGroupOptions & { signal?: AbortSignal } = {},
): Promise<GammaEventWindowGroup> {
  const currentEvent = await fetchGammaEventBySlug(slug, options.signal);
  const seriesSlug = getGammaEventSeriesSlug(currentEvent);

  if (!seriesSlug) {
    return buildGammaEventSlugGroup(currentEvent, [], options);
  }

  const recurrence = currentEvent.series?.[0]?.recurrence;
  const recurrenceMs = getGammaEventRecurrenceMs(recurrence);
  const radius = options.radius ?? 20;
  const currentTimeMs = getGammaEventTimeMs(currentEvent);
  const rangeMs =
    recurrence?.toLowerCase() === 'monthly'
      ? 31 * 24 * 60 * 60 * 1000 * radius
      : recurrenceMs != null
        ? recurrenceMs * radius + recurrenceMs / 2
        : 45 * 24 * 60 * 60 * 1000;
  const limit = options.limit ?? Math.max(50, radius + 10);
  const tagSlug = options.tagSlug ?? pickGammaEventLookupTagSlug(currentEvent);
  const tagId = getCurrentEventCryptoTagId(currentEvent);
  const commonParams = {
    limit,
    order: 'endDate',
    tag_id: tagId,
    archived: false,
    tag_slug: tagSlug,
    recurrence,
  };

  const [pastEvents, futureEvents] = await Promise.all([
    fetchGammaEvents(
      {
        ...commonParams,
        ascending: false,
        end_date_min: new Date(currentTimeMs - rangeMs).toISOString(),
        end_date_max: new Date(currentTimeMs).toISOString(),
      },
      options.signal,
    ),
    fetchGammaEvents(
      {
        ...commonParams,
        ascending: true,
        end_date_min: new Date(currentTimeMs).toISOString(),
        end_date_max: new Date(currentTimeMs + rangeMs).toISOString(),
      },
      options.signal,
    ),
  ]);

  return buildGammaEventSlugGroup(currentEvent, [...pastEvents, ...futureEvents], options);
}

async function fetchGammaEventBySlug(slug: string, signal?: AbortSignal): Promise<GammaEventLike> {
  const searchParams = new URLSearchParams({ slug });
  const response = await fetch(`${POLYMARKET_GAMMA_EVENTS_API}?${searchParams.toString()}`, {
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Polymarket event: ${response.status}`);
  }

  const events = await response.json();
  return events[0] as GammaEventLike;
}

async function fetchGammaEvents(
  params: Record<string, string | number | boolean | undefined>,
  signal?: AbortSignal,
): Promise<GammaEventLike[]> {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    searchParams.set(key, String(value));
  }

  const response = await fetch(`${POLYMARKET_GAMMA_EVENTS_API}?${searchParams.toString()}`, {
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Polymarket events: ${response.status}`);
  }

  const events = await response.json();
  return Array.isArray(events) ? (events as GammaEventLike[]) : [];
}

function getGammaEventSeriesSlug(event: GammaEventLike): string | undefined {
  return event.seriesSlug || event.series?.[0]?.slug;
}

function getGammaEventRecurrenceMs(recurrence?: string): number | null {
  switch (recurrence?.toLowerCase()) {
    case 'hourly':
      return 60 * 60 * 1000;
    case 'daily':
      return 24 * 60 * 60 * 1000;
    case 'weekly':
      return 7 * 24 * 60 * 60 * 1000;
    default:
      return null;
  }
}

function getGammaEventTimeMs(event: GammaEventLike): number {
  const time = Date.parse(
    event.endDate || event.endDateIso || event.startDate || event.createdAt || '',
  );
  return Number.isNaN(time) ? 0 : time;
}

function getGammaEventStartMs(event: GammaEventLike): number {
  const time = Date.parse(event.startDate || event.startDateIso || event.createdAt || '');
  return Number.isNaN(time) ? 0 : time;
}

function toGammaEventWindow(event: GammaEventLike): GammaEventWindow {
  return {
    slug: event.slug,
    startTs: Math.floor(getGammaEventStartMs(event) / 1000),
    endTs: Math.floor(getGammaEventTimeMs(event) / 1000),
  };
}

function pickGammaEventLookupTagSlug(event: GammaEventLike): string | undefined {
  const tags = event.tags ?? [];
  const specificTag = tags.find(
    (tag) =>
      tag.slug && !GENERIC_EVENT_TAG_SLUGS.has(tag.slug) && !ASSET_EVENT_TAG_SLUGS.has(tag.slug),
  );
  if (specificTag?.slug) return specificTag.slug;

  const forceShowTag = tags.find(
    (tag) => tag.forceShow && tag.slug && !GENERIC_EVENT_TAG_SLUGS.has(tag.slug),
  );
  if (forceShowTag?.slug) return forceShowTag.slug;

  return tags.find((tag) => tag.slug && !GENERIC_EVENT_TAG_SLUGS.has(tag.slug))?.slug;
}

function getCurrentEventCryptoTagId(event: GammaEventLike): number | string | undefined {
  const cryptoSlugs = new Set(Object.values(TOKEN_SLUG_MAP));
  return event.tags?.find((tag) => {
    const slug = tag.slug?.toLowerCase();
    return slug ? cryptoSlugs.has(slug) : false;
  })?.id;
}

function buildGammaEventSlugGroup(
  currentEvent: GammaEventLike,
  events: GammaEventLike[],
  options: FetchGammaEventSlugGroupOptions = {},
): GammaEventWindowGroup {
  const seriesSlug = getGammaEventSeriesSlug(currentEvent);
  if (!seriesSlug) {
    return {
      relatedWindows: options.includeCurrent === false ? [] : [toGammaEventWindow(currentEvent)],
    };
  }

  const sameSeriesEventsBySlug = new Map<string, GammaEventLike>();
  for (const event of events) {
    if (event.slug && getGammaEventSeriesSlug(event) === seriesSlug) {
      sameSeriesEventsBySlug.set(event.slug, event);
    }
  }
  sameSeriesEventsBySlug.set(currentEvent.slug, currentEvent);

  const sameSeriesEvents = Array.from(sameSeriesEventsBySlug.values())
    .filter((event) => event.slug && getGammaEventSeriesSlug(event) === seriesSlug)
    .sort((a, b) => getGammaEventTimeMs(a) - getGammaEventTimeMs(b));

  return {
    relatedWindows: sameSeriesEvents
      .filter((event) => options.includeCurrent !== false || event.slug !== currentEvent.slug)
      .map(toGammaEventWindow),
  };
}

export default useTimeWindowList;
